import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { deriveLibraryTitle, type LibraryItem } from "@/lib/library";

type Props = {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  isEdit: boolean;
  selectedLibraryItem: LibraryItem | null;
  otherExcerptCount: number | null;
  unlinkAlsoDelete: boolean;
  setUnlinkAlsoDelete: (v: boolean) => void;
  unlinking: boolean;
  performUnlink: () => void;
};

export function UnlinkSourceDialog({
  open, onOpenChange, isEdit, selectedLibraryItem, otherExcerptCount,
  unlinkAlsoDelete, setUnlinkAlsoDelete, unlinking, performUnlink,
}: Props) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!unlinking) onOpenChange(next); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unlink source from this excerpt?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {isEdit
                  ? "The link will be removed immediately. The excerpt itself stays put."
                  : "The link will be cleared from this draft."}
              </p>
              {isEdit && selectedLibraryItem && (() => {
                const deletionBlocked = otherExcerptCount !== null && otherExcerptCount > 0;
                const checkingRefs = otherExcerptCount === null;
                return (
                  <label
                    className={`flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm ${
                      deletionBlocked ? "opacity-70 cursor-not-allowed" : "cursor-pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={unlinkAlsoDelete && !deletionBlocked}
                      disabled={checkingRefs || deletionBlocked}
                      onChange={(e) => setUnlinkAlsoDelete(e.target.checked)}
                    />
                    <span>
                      <span className="font-medium text-foreground">
                        Also permanently delete this source from My Library
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        “{deriveLibraryTitle(selectedLibraryItem)}” will be removed from every workspace view.
                      </span>
                      {checkingRefs ? (
                        <span className="mt-1 block text-xs text-muted-foreground">
                          Checking whether other excerpts use this source…
                        </span>
                      ) : deletionBlocked ? (
                        <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
                          Can’t delete: {otherExcerptCount} other excerpt{otherExcerptCount === 1 ? "" : "s"} still
                          {otherExcerptCount === 1 ? " links" : " link"} to this source. Unlink or remove
                          {otherExcerptCount === 1 ? " it" : " them"} first.
                        </span>
                      ) : (
                        <span className="mt-1 block text-xs text-muted-foreground">
                          No other excerpts reference this source — safe to delete.
                        </span>
                      )}
                    </span>
                  </label>
                );
              })()}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={unlinking}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); performUnlink(); }}
            disabled={unlinking}
            className={unlinkAlsoDelete ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : undefined}
          >
            {unlinking ? "Unlinking…" : unlinkAlsoDelete ? "Unlink & delete" : "Unlink"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
